import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { EASE } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [form, setForm] = useState({ nom: "", courriel: "" });
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ token: "", question: "" });
  const [reponse, setReponse] = useState("");

  const chargerCaptcha = async () => {
    try {
      const r = await axios.get(`${API}/captcha`);
      setCaptcha({ token: r.data.token, question: r.data.question });
      setReponse("");
    } catch {
      toast.error("Impossible de charger la vérification. Rafraîchissez la page.");
    }
  };

  useEffect(() => {
    chargerCaptcha();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/infolettre`, {
        ...form,
        captcha_token: captcha.token,
        captcha_reponse: Number(reponse),
      });
      toast.success("Merci! Vous êtes maintenant inscrit à notre infolettre.");
      setForm({ nom: "", courriel: "" });
      chargerCaptcha();
    } catch (err) {
      if (err?.response?.status === 400) {
        toast.error("Réponse à la vérification incorrecte. Réessayez.");
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
      chargerCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: EASE }}
          className="bg-[#4d585f] border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] px-8 md:px-14 py-12 md:py-16 grid md:grid-cols-2 gap-10 md:gap-16 items-center"
        >
          <div>
            <div className="flex items-center gap-3 text-[#D1BFAE]">
              <Mail size={18} strokeWidth={1.5} />
              <span className="uppercase tracking-[0.25em] text-xs">
                Infolettre
              </span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl leading-tight mt-5 text-[#F9F9F9]">
              Restez inspiré, <em className="text-[#D1BFAE]">restez informé</em>
            </h2>
            <p className="text-[#C4C9CE] text-base leading-relaxed mt-4">
              Nos plus belles réalisations, nos conseils de rénovation et nos
              offres exclusives — directement dans votre boîte courriel.
            </p>
          </div>
          <form
            data-testid="newsletter-form"
            onSubmit={submit}
            className="space-y-7"
          >
            <div>
              <label htmlFor="newsletter-nom" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
                Nom *
              </label>
              <input
                id="newsletter-nom"
                data-testid="newsletter-nom-input"
                required
                value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                placeholder="Votre nom"
                className="input-line"
              />
            </div>
            <div>
              <label htmlFor="newsletter-courriel" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
                Courriel *
              </label>
              <input
                id="newsletter-courriel"
                data-testid="newsletter-courriel-input"
                required
                type="email"
                value={form.courriel}
                onChange={(e) => setForm({ ...form, courriel: e.target.value })}
                placeholder="vous@exemple.com"
                className="input-line"
              />
            </div>
            <div>
              <label htmlFor="newsletter-captcha" className="block uppercase tracking-[0.2em] text-xs text-[#C4C9CE] mb-2">
                Vérification anti-robot : {captcha.question}
              </label>
              <input
                id="newsletter-captcha"
                data-testid="newsletter-captcha-input"
                required
                inputMode="numeric"
                value={reponse}
                onChange={(e) => setReponse(e.target.value)}
                placeholder="Votre réponse"
                className="input-line"
              />
            </div>
            <button
              data-testid="newsletter-submit-button"
              type="submit"
              disabled={loading}
              className="relative overflow-hidden w-full border border-[#D1BFAE]/60 px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#D1BFAE] group disabled:opacity-60"
            >
              <span className="absolute inset-0 bg-[#D1BFAE] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="relative group-hover:text-[#1a1a1a] transition-colors duration-300">
                {loading ? "Inscription…" : "S'inscrire à l'infolettre"}
              </span>
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
